// /src/pages/DepartmentsPage.js
// Admin screen for departments and the modules granted to each one.
// Department grants are inherited by every member of the department; grants
// made directly to a user live on the User Management page.
import React, { useEffect, useState } from "react";

function api(path, opts = {}) {
  const token = localStorage.getItem("token");
  return fetch(`/api${path}`, {
    method: opts.method || "GET",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: opts.body ? JSON.stringify(opts.body) : undefined,
  }).then(async (res) => {
    if (!res.ok) {
      const text = await res.text();
      let msg = text;
      try { msg = JSON.parse(text).error || text; } catch { /* keep raw text */ }
      throw new Error(msg || `${res.status} ${res.statusText}`);
    }
    return res.status === 204 ? null : res.json();
  });
}

export default function DepartmentsPage() {
  const [departments, setDepartments] = useState([]);
  const [modules, setModules] = useState([]);
  const [grants, setGrants] = useState({});   // deptId -> [moduleId]
  const [open, setOpen] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  async function load() {
    try {
      setLoading(true);
      setErr("");
      const [d, m] = await Promise.all([api(`/departments`), api(`/modules`)]);
      setDepartments(d || []);
      setModules(m || []);
    } catch (e) {
      setErr(e.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  async function createDept(e) {
    e.preventDefault();
    const f = new FormData(e.target);
    const name = (f.get("name") || "").trim();
    if (!name) return;
    try {
      setErr("");
      await api(`/departments`, { method: "POST", body: { name } });
      e.target.reset();
      load();
    } catch (e2) {
      setErr(e2.message);
    }
  }

  async function updateDept(id, patch) {
    try {
      setErr("");
      await api(`/departments/${id}`, { method: "PUT", body: patch });
      load();
    } catch (e) {
      setErr(e.message);
      load();   // resync so the row reflects what the server actually holds
    }
  }

  async function deleteDept(d) {
    const warning =
      d.member_count > 0
        ? `Delete "${d.name}"?\n\n${d.member_count} user(s) will be left with no ` +
          `department. They are not deleted, but they lose any module access ` +
          `they inherited from this department.`
        : `Delete "${d.name}"?`;
    if (!window.confirm(warning)) return;
    try {
      setErr("");
      await api(`/departments/${d.id}`, { method: "DELETE" });
      if (open === d.id) setOpen(null);
      load();
    } catch (e) {
      setErr(e.message);
    }
  }

  async function toggleGrants(deptId) {
    if (open === deptId) { setOpen(null); return; }
    setOpen(deptId);
    if (!grants[deptId]) {
      try {
        const res = await api(`/departments/${deptId}/modules`);
        setGrants((g) => ({ ...g, [deptId]: res.module_ids || [] }));
      } catch (e) {
        setErr(e.message);
      }
    }
  }

  async function setGrant(deptId, moduleId, checked) {
    const current = grants[deptId] || [];
    const next = checked
      ? [...new Set([...current, moduleId])]
      : current.filter((id) => id !== moduleId);
    setGrants((g) => ({ ...g, [deptId]: next }));   // optimistic
    try {
      await api(`/departments/${deptId}/modules`, { method: "PUT", body: { module_ids: next } });
    } catch (e) {
      setErr(e.message);
      setGrants((g) => ({ ...g, [deptId]: current }));   // roll back on failure
    }
  }

  return (
    <div style={{ padding: "16px" }}>
      <h2>Departments</h2>
      <p style={{ opacity: 0.75, marginTop: -6 }}>
        Modules granted to a department are available to every member of it.
        Grants made to an individual are managed on the User Management page.
      </p>

      <form onSubmit={createDept} style={{ display: "flex", gap: 8, margin: "12px 0" }}>
        <input name="name" placeholder="New department name" style={inp} />
        <button style={btnPrimary}>Add</button>
      </form>

      {err && <div style={{ color: "#f87171", marginBottom: 10 }}>{err}</div>}

      <div style={{ border: "1px solid #26324f", borderRadius: 8, overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ background: "#0c1326" }}>
              {["Name", "Members", "Active", "Actions"].map((h) => (
                <th key={h} style={th}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan="4" style={td}>Loading…</td></tr>}
            {!loading && departments.length === 0 && (
              <tr><td colSpan="4" style={td}>No departments yet.</td></tr>
            )}
            {!loading && departments.map((d) => (
              <React.Fragment key={d.id}>
                <tr>
                  <td style={td}>
                    <input
                      defaultValue={d.name}
                      onBlur={(e) => {
                        const v = e.target.value.trim();
                        if (v && v !== d.name) updateDept(d.id, { name: v });
                      }}
                      style={cellInput}
                    />
                  </td>
                  <td style={td}>{d.member_count}</td>
                  <td style={td}>
                    <input
                      type="checkbox"
                      defaultChecked={d.is_active}
                      onChange={(e) => updateDept(d.id, { is_active: e.target.checked })}
                    />
                  </td>
                  <td style={{ ...td, whiteSpace: "nowrap" }}>
                    <button onClick={() => toggleGrants(d.id)} style={btnGhost}>
                      {open === d.id ? "Hide modules" : "Module access"}
                    </button>{" "}
                    <button onClick={() => deleteDept(d)} style={btnDanger}>Delete</button>
                  </td>
                </tr>

                {open === d.id && (
                  <tr>
                    <td colSpan="4" style={{ ...td, background: "#0c1326" }}>
                      {modules.length === 0 ? (
                        <em>No modules configured.</em>
                      ) : (
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 14 }}>
                          {modules.map((m) => (
                            <label key={m.id} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                              <input
                                type="checkbox"
                                checked={(grants[d.id] || []).includes(m.id)}
                                onChange={(e) => setGrant(d.id, m.id, e.target.checked)}
                              />
                              {m.name}
                              {m.status !== "active" && (
                                <span style={{ opacity: 0.6 }}> (not licensed)</span>
                              )}
                            </label>
                          ))}
                        </div>
                      )}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/*** Styles (matching UsersPage) ***/
const inp = { padding:"8px 10px", borderRadius:8, border:"1px solid #334155", background:"#0f172a", color:"#e6eefc" };
const cellInput = { ...inp, padding:"4px 6px", width:"100%" };
const th = { textAlign:"left", padding:"10px 12px", borderBottom:"1px solid #26324f" };
const td = { padding:"10px 12px", borderBottom:"1px solid #1e293b" };
const btnPrimary = { padding:"8px 12px", background:"#2979ff", color:"#fff", border:"1px solid #2a6bff", borderRadius:8, cursor:"pointer" };
const btnGhost = { padding:"6px 10px", background:"transparent", color:"#e6eefc", borderRadius:8, border:"1px solid #334155", cursor:"pointer" };
const btnDanger = { padding:"6px 10px", background:"#991b1b", color:"#fff", border:"1px solid #7f1d1d", borderRadius:8, cursor:"pointer" };
